import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVendorApplications, approveVendor, rejectVendor } from "../../redux/actions/vendor";
import { toast } from "react-toastify";
import {
  HiOutlineBadgeCheck,
  HiOutlineX,
  HiOutlineEye,
  HiOutlineSearch,
  HiOutlineFilter,
} from "react-icons/hi";

const AdminVendorApprovals = () => {
  const dispatch = useDispatch();
  const { vendors, isLoading, total, pages, success, error } = useSelector((state) => state.vendor);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    dispatch(getVendorApplications(statusFilter, page));
  }, [dispatch, statusFilter, page]);

  useEffect(() => {
    if (success) {
      toast.success("Action completed successfully");
      dispatch({ type: "clearVendorSuccess" });
      dispatch(getVendorApplications(statusFilter, page));
    }
    if (error) {
      toast.error(error);
      dispatch({ type: "clearVendorErrors" });
    }
  }, [success, error]);

  const handleApprove = (vendorId) => {
    dispatch(approveVendor(vendorId));
  };

  const handleReject = () => {
    if (!selectedVendor) return;
    dispatch(rejectVendor(selectedVendor._id, rejectReason));
    setShowRejectModal(false);
    setRejectReason("");
    setSelectedVendor(null);
  };

  const statusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen bg-secondary-50">
      <div className="mb-8 pb-6 border-b border-secondary-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin</p>
            <h1 className="text-3xl font-bold text-black">Vendor Approvals</h1>
            <p className="text-secondary-500 text-sm mt-1">Manage vendor applications and approvals</p>
          </div>
          <div className="flex gap-2">
            {["pending", "approved", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                  statusFilter === s
                    ? "bg-black text-white"
                    : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111111]" />
        </div>
      ) : (
        <div className="bg-white border border-secondary-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">GST</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Applied</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {vendors && vendors.length > 0 ? vendors.map((vendor) => (
                  <tr key={vendor._id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {vendor.avatar?.url ? (
                          <img src={vendor.avatar.url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#111111]/10 flex items-center justify-center text-[#111111] font-bold">
                            {vendor.name?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{vendor.name}</p>
                          <p className="text-sm text-gray-500">{vendor.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{vendor.businessName || vendor.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{vendor.phoneNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{vendor.gstNumber || "N/A"}</td>
                    <td className="px-4 py-3">{statusBadge(vendor.approvalStatus)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(vendor.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {vendor.approvalStatus === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(vendor._id)}
                              className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                              title="Approve"
                            >
                              <HiOutlineBadgeCheck className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => { setSelectedVendor(vendor); setShowRejectModal(true); }}
                              className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                              title="Reject"
                            >
                              <HiOutlineX className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                      No {statusFilter} vendor applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-gray-200">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    page === p ? "bg-[#111111] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Vendor Application</h3>
            <p className="text-sm text-gray-500 mb-3">
              Rejecting vendor: <strong>{selectedVendor?.name}</strong>
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent resize-none"
              rows="4"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Reject Vendor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendorApprovals;
